import React, { Component } from 'react';

import PropTypes from 'prop-types';
import { hot } from 'react-hot-loader/root';
import { compose } from 'redux';

import { DevelopersCompanyContext } from 'common/containers/DevelopersCompanyContainer';
import MultiLanguageCodeBlock from 'common/developers/docs/widget/MultiLanguageCodeBlock';
import AngularEmbedSnippet from 'common/developers/docs/widget/snippets/AngularEmbedSnippet';
import AngularRoutingSnippet from 'common/developers/docs/widget/snippets/AngularRoutingSnippet';
import RailsRoutingSnippet from 'common/developers/docs/widget/snippets/RailsRoutingSnippet';
import ReactEmbedSnippet from 'common/developers/docs/widget/snippets/ReactEmbedSnippet';
import ReactRoutingSnippet from 'common/developers/docs/widget/snippets/ReactRoutingSnippet';
import VueEmbedSnippet from 'common/developers/docs/widget/snippets/VueEmbedSnippet';
import VueRoutingSnippet from 'common/developers/docs/widget/snippets/VueRoutingSnippet';
import WebEmbedSnippet from 'common/developers/docs/widget/snippets/WebEmbedSnippet';
import WidgetDocsWarning from 'common/developers/docs/widget/WidgetDocsWarning';
import WidgetDocsWebHelmet from 'common/helmets/WidgetDocsWebHelmet';
import Link from 'common/Link';
import BoardPicker from 'common/subdomain/admin/BoardPicker';
import withContexts from 'common/util/withContexts';

import 'css/components/developers/docs/widget/_WidgetDocsWeb.scss';

class WidgetDocsWeb extends Component {
  static propTypes = {
    company: PropTypes.shape({
      boards: PropTypes.array,
    }),
  };

  state = {
    board: null,
  };

  onBoardSelected = (board) => {
    this.setState({
      board,
    });
  };

  render() {
    const { company } = this.props;
    const boards = company ? company.boards : null;
    const { board } = this.state;

    return (
      <div className="widgetDocsWeb">
        <WidgetDocsWebHelmet />
        <WidgetDocsWarning />
        <div className="title">Web Widget</div>
        <div className="intro">
          These are instructions on how to embed Canny's widget into your website.
        </div>
        <ol className="numberedList">
          <li>
            <div className="subheading">1. Embed the widget</div>
            <div className="contents">
              Add the following code where you'd like to embed the widget, anywhere inside the{' '}
              <span className="code">&lt;body&gt;</span> tag.
            </div>
            {boards ? (
              <BoardPicker
                boards={boards}
                placeholder="Fill in board token for:"
                onBoardSelected={this.onBoardSelected}
              />
            ) : (
              <div className="contents">
                <span className="bold">To find your board token</span>, log in above and select your
                board from the&nbsp;dropdown.
              </div>
            )}
            <div className="contents">
              <MultiLanguageCodeBlock
                blocks={[
                  {
                    code: board
                      ? WebEmbedSnippet.replace('YOUR_BOARD_TOKEN', board.token)
                      : WebEmbedSnippet,
                    language: 'html',
                    name: 'HTML',
                  },
                  {
                    code: board
                      ? ReactEmbedSnippet.replace('YOUR_BOARD_TOKEN', board.token)
                      : ReactEmbedSnippet,
                    language: 'js',
                    name: 'React',
                  },
                  {
                    code: board
                      ? AngularEmbedSnippet.replace('YOUR_BOARD_TOKEN', board.token)
                      : AngularEmbedSnippet,
                    language: 'js',
                    name: 'Angular',
                  },
                  {
                    code: board
                      ? VueEmbedSnippet.replace('YOUR_BOARD_TOKEN', board.token)
                      : VueEmbedSnippet,
                    language: 'html',
                    name: 'Vue.js',
                  },
                ]}
              />
            </div>
            <div className="contents">
              You should now be able to load the widget. It's that easy! 😃
            </div>
          </li>
          <li>
            <div className="subheading">
              2. Configure the <span className="code">basePath</span> parameter for URL Syncing
            </div>
            <div className="contents">
              This syncs the URLs inside our widget with your website. For example, if our widget is
              embedded at yourwebsite.com/feedback then you'd pass in{' '}
              <span className="code">'/feedback'</span>.
            </div>
            <div className="contents">
              Also don't forget to update your routing so that /feedback/* points to the same page.
            </div>
            <div className="contents">
              <MultiLanguageCodeBlock
                blocks={[
                  {
                    code: RailsRoutingSnippet,
                    language: 'ruby',
                    name: 'Rails',
                  },
                  {
                    code: ReactRoutingSnippet,
                    language: 'js',
                    name: 'React',
                  },
                  {
                    code: AngularRoutingSnippet,
                    language: 'js',
                    name: 'Angular',
                  },
                  {
                    code: VueRoutingSnippet,
                    language: 'js',
                    name: 'Vue.js',
                  },
                ]}
              />
            </div>
          </li>
          <li>
            <div className="subheading">
              3. Configure the <span className="code">ssoToken</span> parameter for Single Sign-On
            </div>
            <div className="contents">
              Single Sign-On connects the widget to your existing user accounts. This provides a
              more seamless experience for your users. It also lets you know exactly who is posting
              and voting.
            </div>
            <div className="contents">
              Follow our <Link to="/install/widget/sso">Setup Single Sign-On</Link> guide to
              generate SSO tokens on your server.
            </div>
            <div className="contents">
              This is required if you would like to install the widget with a private board.
            </div>
          </li>
          <li>
            <div className="subheading">Questions</div>
            <div className="contents">
              If you have any questions or issues, email us at{' '}
              <a href="mailto:support@canny.io">support@canny.io</a> and we'll get back to you ASAP.
            </div>
          </li>
        </ol>
      </div>
    );
  }
}

export default compose(withContexts({ company: DevelopersCompanyContext }), hot)(WidgetDocsWeb);
